import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Send, Edit3, Loader, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { engineApiClient } from "@/lib/apiClient";
import { trackEvent } from "@/lib/monitoring";
import type { OutreachDraft } from "@/lib/contracts";

export function OutreachPreview() {
  const { id, candidateId } = useParams<{ id: string; candidateId: string }>();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<OutreachDraft | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  const projectId = id ?? "";
  const candId = candidateId ?? "";

  useEffect(() => {
    if (!projectId || !candId) return;
    setIsLoading(true);
    engineApiClient
      .generateOutreach(projectId, candId)
      .then((res) => {
        setDraft(res.draft);
        setSubject(res.draft.emailSubject);
        setBody(res.draft.emailBody);
      })
      .catch(() => setError("Failed to generate outreach message. Please try again."))
      .finally(() => setIsLoading(false));
  }, [projectId, candId]);

  const handleSend = async () => {
    setIsSending(true);
    try {
      // Save current edits first so the email matches what the recruiter approved
      await engineApiClient.updateOutreachDraft(projectId, candId, subject, body);
      await engineApiClient.sendOutreach(projectId, candId);
      trackEvent('outreach_sent', { projectId, candidateId: candId });
      setSent(true);
      setTimeout(() => navigate(`/project/${id}/outreach`), 1200);
    } catch {
      setError("Send failed. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveDraft = () => {
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#02182B] flex items-center justify-center gap-3 text-gray-400">
        <Loader size={20} className="animate-spin text-[#FD802E]" />
        <span>Generating outreach message…</span>
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="min-h-screen bg-[#02182B] flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
          <AlertCircle size={24} className="text-red-400" />
        </div>
        <p className="text-red-400 text-sm font-medium">{error ?? "Outreach draft unavailable."}</p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/project/${id}/candidate/${candidateId}`)}
            className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
          >
            ← Go back
          </button>
          <button
            onClick={() => { setError(null); window.location.reload(); }}
            className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#02182B]">
      <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/project/${id}/candidate/${candidateId}`} className="text-gray-400 hover:text-white transition-colors">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-white text-xl font-semibold">Outreach Preview</h1>
                <p className="text-gray-400 text-sm">Review and approve message before sending</p>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center gap-2 px-4 py-2 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors text-sm"
            >
              <Edit3 size={16} />{isEditing ? "Preview" : "Edit"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Candidate / info sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6 sticky top-24 space-y-4">
              <h3 className="text-white font-semibold">Outreach Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs mb-1">Calendar link included</p>
                  <p className="text-[#FD802E] text-xs font-mono break-all">{draft.calendarLink}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">LinkedIn version</p>
                  <p className="text-gray-300 text-xs italic">"{draft.linkedinMessage}"</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Generated</p>
                  <p className="text-gray-400 text-xs">{new Date(draft.generatedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Message editor */}
          <div className="lg:col-span-2">
            <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] overflow-hidden">
              <div className="bg-[#02182B] border-b border-[#2a4a5c] p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="text-[#FD802E]" size={20} />
                  <span className="text-white font-medium">Email Preview</span>
                </div>
                <div className="space-y-2">
                  <label className="text-gray-400 text-xs">Subject</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#FD802E] transition-colors text-sm"
                    />
                  ) : (
                    <div className="text-white font-medium text-sm">{subject}</div>
                  )}
                </div>
              </div>

              <div className="p-6">
                {isEditing ? (
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={16}
                    className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg p-4 text-white focus:outline-none focus:border-[#FD802E] text-sm resize-none"
                  />
                ) : (
                  <div className="text-white whitespace-pre-wrap leading-relaxed text-sm">{body}</div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <Link
                to={`/project/${id}/candidate/${candidateId}`}
                className="px-6 py-3 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors font-medium text-sm"
              >
                Cancel
              </Link>
              <button
                onClick={handleSaveDraft}
                className="px-6 py-3 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors font-medium text-sm"
              >
                {draftSaved ? "✓ Saved" : "Save Draft"}
              </button>
              <button
                onClick={handleSend}
                disabled={isSending || sent}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold disabled:opacity-70 text-sm"
              >
                <Send size={16} />
                {sent ? "✓ Sent! Redirecting…" : isSending ? "Sending…" : "Approve & Send"}
              </button>
            </div>

            {error && (
              <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
